import { useTranslations } from '@vocab/react';
import { Card as BraidCard } from 'braid-design-system';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Card } from 'src/components/Card/Card';
import { CardAction } from 'src/components/Card/CardAction/CardAction';
import { FormDrawer } from 'src/components/FormDrawer/FormDrawer';
import { LoadingItem } from 'src/components/LoadingBlock/LoadingBlock';
import { LANGUAGES } from 'src/components/Router';
import { updateRoute } from 'src/components/Router/actionCreators';
import { useTypedSelector } from 'src/modules/hooks/useTypedSelector';
import {
  getFailedActionsForParent,
  retryParent,
} from 'src/modules/redux-blend';
import { LANGUAGES_FORM } from 'src/reducers/forms';

import translations from './.vocab';
import { EditLanguages } from './EditLanguages';
import { Languages } from './Languages';
import { useLanguages } from './data';

export const ReadLanguages = () => {
  const { t } = useTranslations(translations);
  const { languages, loading, error } = useLanguages();
  const [drawerTitle, setDrawerTitle] = useState<string>('');

  const blend = useTypedSelector((state) => state.blend);
  const page = useTypedSelector((state) => state.router.page);
  const mode = useTypedSelector((store) => store.router[LANGUAGES].mode);
  const languageId = useTypedSelector(
    (store) => store.router[LANGUAGES].params.id,
  );
  const isCreateResumePage = page === 'create-resume-page';

  const isEmpty = languages.length === 0;

  useEffect(() => {
    if (mode === 'EDIT') {
      if (languageId) {
        setDrawerTitle(t('Edit language'));
      } else {
        setDrawerTitle(t('Add language'));
      }
    }
  }, [mode, t, languageId]);

  const cardAction = (
    <CardAction
      to="languages"
      mode="EDIT"
      actionText={t('Add language')}
      data={{ automation: 'language-add' }}
      isEmpty={isEmpty}
      analytics={{
        currentPageSectionState: 'add',
      }}
    />
  );

  const dispatch = useDispatch();

  const editLanguage = useCallback(
    (id: string) => {
      dispatch(
        updateRoute({
          to: 'languages',
          mode: 'EDIT',
          params: { id },
          analytics: {
            currentPageSectionState: 'edit',
          },
        }),
      );
    },
    [dispatch],
  );
  const handleRetry = useCallback(
    () => dispatch(retryParent(LANGUAGES)),
    [dispatch],
  );

  const showRetry = getFailedActionsForParent({ blend }, LANGUAGES).length > 0;

  const isLoading = Boolean(loading || error);

  return (
    <>
      <Card
        name="languages"
        title={t('Languages')}
        isEmpty={isEmpty}
        cardAction={cardAction}
        retryAction={showRetry ? handleRetry : undefined}
        emptyCardText={t(
          'Add languages to appeal to more companies and employers.',
        )}
        showOptionalBadge={isCreateResumePage}
        loading={isLoading}
        loader={
          <BraidCard>
            <LoadingItem type="text" size="standard" width="medium" />
          </BraidCard>
        }
      >
        <Languages
          displayVariant="card"
          languages={languages.map((language) => ({
            ...language,
            editLanguage,
          }))}
        />
      </Card>
      <FormDrawer
        title={drawerTitle}
        component={EditLanguages}
        card={LANGUAGES}
        formName={LANGUAGES_FORM}
      />
    </>
  );
};
